// src/components/products/components/ProductForm.jsx

import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  PhotoIcon, 
  TrashIcon,
  CloudArrowUpIcon, 
  XMarkIcon,
  Bars3Icon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { METAL_TYPES, GEMSTONE_TYPES, GEMSTONE_COLORS, SETTING_TYPES } from '../constants';
import FormField from '../ui/FormField';

const ImageDeleteModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-fadeIn">
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center mb-4">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Image</h3>
          <p className="text-gray-600 mb-6">
            Are you sure you want to delete this image? This action cannot be undone.
          </p>
          <div className="flex gap-3 w-full">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-900 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// React Quill configuration
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['clean']
  ]
};

const quillFormats = [
  'bold', 'italic', 'underline',
  'list', 'bullet'
];

const ProductForm = ({ product, onSave, onCancel, onDelete, isNew = false }) => {
  const getInitialFormData = (productData = {}) => ({
    imageUrls: [],
    name: '',
    price: '',
    metalType: '',
    gemstoneType: '',
    gemstoneColor: '',
    settingType: '',
    description: '',
    status: 'draft',
    ...productData,
    availablePurchase: productData.availablePurchase ?? (isNew ? '' : true)
  });

  const [formData, setFormData] = useState(getInitialFormData(product));

  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [imageIdCounter, setImageIdCounter] = useState(0);
  const [draggedImage, setDraggedImage] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [errors, setErrors] = useState({});
  const [imageToDelete, setImageToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadDragActive, setUploadDragActive] = useState(false);


  const allImages = [
    ...(formData.imageUrls || []).map((url, index) => ({
      id: `existing-${url}`,
      type: 'existing',
      url,
      originalIndex: index
    })),
    ...newImagePreviews.map((preview, index) => ({
      id: preview.id,
      type: 'new',
      url: preview.url,
      file: preview.file,
      name: preview.name,
      originalIndex: index
    }))
  ];

  useEffect(() => {
    setFormData(getInitialFormData(product));
    setNewImageFiles([]);
    setNewImagePreviews([]);
    setImageIdCounter(0);
  }, [product]);

  useEffect(() => {
    return () => {
      newImagePreviews.forEach(preview => {
        if (preview.url && preview.url.startsWith('blob:')) {
          URL.revokeObjectURL(preview.url);
        }
      });
    };
  }, [newImagePreviews]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const normalizeWholeNumber = (value) => {
    if (value === null || value === undefined) return '';
    const raw = value.toString();
    const wholePart = raw.split('.')[0];
    const digitsOnly = wholePart.replace(/[^\d]/g, '');
    return digitsOnly;
  };

  const handlePriceChange = (value) => {
    const normalized = normalizeWholeNumber(value);
    handleInputChange('price', normalized);
  };

  const handleImageFilesChange = (e) => {
    addImageFiles(e.target.files);
  };

  const addImageFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const previews = files.map((file, index) => ({
      id: `new-${imageIdCounter + index}-${Date.now()}`,
      file,
      url: URL.createObjectURL(file),
      name: file.name
    }));

    setImageIdCounter(prev => prev + files.length);
    setNewImageFiles(prev => [...prev, ...files]);
    setNewImagePreviews(prev => [...prev, ...previews]);
  };

  const handleUploadDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setUploadDragActive(true);
    } else if (e.type === 'dragleave') {
      setUploadDragActive(false);
    }
  };

  const handleUploadDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadDragActive(false);
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      addImageFiles(e.dataTransfer.files);
    }
  };

  const handleRemoveNewImage = (imageIndex) => {
    const newImageStartIndex = formData.imageUrls?.length || 0;
    const actualNewImageIndex = imageIndex - newImageStartIndex;
    
    if (newImagePreviews[actualNewImageIndex].url && newImagePreviews[actualNewImageIndex].url.startsWith('blob:')) {
      URL.revokeObjectURL(newImagePreviews[actualNewImageIndex].url);
    }
    
    setNewImageFiles(prev => prev.filter((_, i) => i !== actualNewImageIndex));
    setNewImagePreviews(prev => prev.filter((_, i) => i !== actualNewImageIndex));
  };

  const handleDeleteExistingImage = (imageIndex) => {
    const urlToDelete = formData.imageUrls[imageIndex];
    setImageToDelete(urlToDelete);
  };

  const handleConfirmDeleteImage = () => {
    setFormData(prev => ({
      ...prev,
      imageUrls: prev.imageUrls.filter(url => url !== imageToDelete)
    }));
    setImageToDelete(null);
  };

  const handleDragStart = (e, index) => {
    setDraggedImage(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (draggedImage === null || draggedImage === dropIndex) {
      setDraggedImage(null);
      return;
    }

    const draggedImg = allImages[draggedImage];
    const newOrder = [...allImages];
    
    newOrder.splice(draggedImage, 1);
    newOrder.splice(dropIndex, 0, draggedImg);
    
    const reorderedExisting = [];
    const reorderedNew = [];
    const reorderedNewFiles = [];
    
    newOrder.forEach(img => {
      if (img.type === 'existing') {
        reorderedExisting.push(img.url);
      } else {
        const originalPreview = newImagePreviews.find(p => p.id === img.id);
        if (originalPreview) {
          reorderedNew.push(originalPreview);
          reorderedNewFiles.push(originalPreview.file);
        }
      }
    });
    
    setFormData(prev => ({ ...prev, imageUrls: reorderedExisting }));
    setNewImagePreviews(reorderedNew);
    setNewImageFiles(reorderedNewFiles);
    setDraggedImage(null);
  };

  const handleDragEnd = () => {
    setDraggedImage(null);
    setDragOverIndex(null);
  };

  const validateForm = (status) => {
    const newErrors = {};

    if (formData.availablePurchase !== true && formData.availablePurchase !== false) {
      newErrors.availablePurchase = 'Select whether this ring is available to purchase';
    }
    
    if (status === 'live') {
      const hasImages = (formData.imageUrls && formData.imageUrls.length > 0) || newImageFiles.length > 0;
      if (!hasImages) {
        newErrors.images = 'At least one image is required to publish';
      }
      
      if (!formData.name || formData.name.trim() === '') {
        newErrors.name = 'Product name is required to publish';
      }
      
      if (!formData.price || formData.price === '') {
        newErrors.price = 'Price is required to publish';
      } else if (isNaN(formData.price) || parseFloat(formData.price) <= 0) {
        newErrors.price = 'Price must be a valid positive number';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (status) => {
    if (!validateForm(status)) return;
    
    setIsSubmitting(true);
    try {
      await onSave({ 
        ...formData, 
        status,
        newImageFiles: newImageFiles
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalImages = allImages.length;

  // Inline styles using CSS variables
  const styles = {
    formContainer: {
      background: `linear-gradient(to bottom right, var(--Surface-Secondary), var(--Grey-100))`,
      padding: 'var(--spacing-m)',
      borderRadius: 'var(--radius-s)'
    },
    input: {
      width: '100%',
      padding: 'var(--spacing-xs) var(--spacing-s)',
      border: 'var(--input-border)',
      borderRadius: 'var(--input-radius)',
      fontSize: 'var(--font-size-body3)',
      color: 'var(--Text-Primary)',
      background: 'var(--Surface-Primary)',
      fontFamily: 'var(--font-family-primary)'
    },
    button: {
      padding: 'var(--spacing-xs) var(--spacing-m)',
      borderRadius: 'var(--button-radius)',
      fontSize: 'var(--font-size-button)',
      fontWeight: 'var(--button-font-weight)',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: 'var(--font-family-primary)'
    }
  };

  return (
    <>
      <ImageDeleteModal
        isOpen={!!imageToDelete}
        onClose={() => setImageToDelete(null)}
        onConfirm={handleConfirmDeleteImage}
      />
      
      <div style={styles.formContainer}>
        {/* Images Section */}
        <FormField 
          label="Product Images" 
          required={false}
          error={errors.images}
          tooltipText="Drag images to reorder. The first image will be the main product image."
        >
          <div style={{ marginBottom: 'var(--spacing-m)' }}>
            {/* Instructions */}
            <div className="flex justify-between items-start mb-2">
              <div className="space-y-1">
                {totalImages > 0 && (
                  <p className="text-sm" style={{ color: 'var(--Text-Secondary)' }}>
                    {formData.imageUrls?.length || 0} saved image{formData.imageUrls?.length !== 1 ? 's' : ''}
                    {newImagePreviews.length > 0 && (
                      <span style={{ color: 'var(--Green-700)', fontWeight: 'var(--font-weight-medium)' }}>
                        {' '}+ {newImagePreviews.length} new image{newImagePreviews.length !== 1 ? 's' : ''} to upload
                      </span>
                    )}
                  </p>
                )}
                <p className="text-xs flex items-center gap-1" style={{ color: 'var(--Text-Tertiary)' }}>
                  <Bars3Icon className="w-3 h-3" />
                  Drag to reorder images
                </p>
              </div>
              {totalImages > 0 && (
                <div style={{
                  background: 'var(--Amber-500)',
                  color: 'black',
                  padding: '2px 8px',
                  borderRadius: 'var(--radius-m)',
                  fontSize: 'var(--font-size-body5)',
                  fontWeight: 'var(--font-weight-medium)'
                }}>
                  First image = Main display
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {/* All images */}
              {allImages.map((image, index) => (
                <div
                  key={image.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`
                    relative group cursor-move rounded-lg overflow-hidden
                    ${index === 0 ? 'col-span-2 row-span-2' : 'col-span-1'}
                    ${dragOverIndex === index ? 'opacity-50 ring-2 ring-purple-500' : ''}
                    ${draggedImage === index ? 'opacity-30' : ''}
                    transition-all duration-200
                  `}
                  style={{ aspectRatio: '1' }}
                >
                  {/* Main image indicator */}
                  {index === 0 && (
                    <div className="absolute inset-0 ring-4 ring-amber-400 rounded-lg pointer-events-none z-10" />
                  )}
                  
                  <img 
                    src={image.url} 
                    alt={`Product ${index + 1}`} 
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                    {/* Drag handle */}
                    <div className="absolute top-2 left-2 bg-white/20 backdrop-blur-sm text-white rounded p-1">
                      <Bars3Icon className="w-4 h-4" />
                    </div>
                    
                    {/* Delete button */}
                    <button 
                      type="button"
                      onClick={() => {
                        if (image.type === 'existing') {
                          handleDeleteExistingImage(image.originalIndex);
                        } else {
                          handleRemoveNewImage(index);
                        }
                      }}
                      className="absolute top-2 right-2 p-1.5 rounded-full transition-colors"
                      style={{ background: 'var(--Red-600)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--Red-700)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--Red-600)'}
                    >
                      {image.type === 'existing' ? 
                        <TrashIcon className="w-4 h-4 text-white" /> : 
                        <XMarkIcon className="w-4 h-4 text-white" />
                      }
                    </button>
                  </div>
                  
                  {/* Labels */}
                  <div className="absolute bottom-2 left-2 flex gap-1">
                    {index === 0 && (
                      <div className="px-2 py-1 text-xs font-semibold rounded"
                           style={{ background: 'var(--Amber-500)', color: 'black', borderRadius: 'var(--radius-l)' }}>
                        Main Image
                      </div>
                    )}
                    {image.type === 'new' && (
                      <div className="px-2 py-1 text-xs font-semibold rounded text-black"
                           style={{ background: 'var(--brand-gradient-4)', borderRadius: 'var(--radius-l)' }}>
                        New
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Upload button */}
              <label 
                htmlFor="image-upload" 
                className="flex flex-col items-center justify-center rounded-lg p-3 cursor-pointer aspect-square transition-colors group"
                style={{
                  border: `2px dashed ${uploadDragActive ? 'var(--Blue-500)' : 'var(--Grey-500)'}`,
                  background: uploadDragActive ? 'var(--Blue-50)' : 'var(--Surface-Primary)'
                }}
                onMouseEnter={e => {
                  if (!uploadDragActive) {
                    e.currentTarget.style.background = 'var(--Grey-50)';
                    e.currentTarget.style.borderColor = 'var(--Grey-400)';
                  }
                }}
                onMouseLeave={e => {
                  if (!uploadDragActive) {
                    e.currentTarget.style.background = 'var(--Surface-Primary)';
                    e.currentTarget.style.borderColor = 'var(--Grey-500)';
                  }
                }}
                onDragEnter={handleUploadDrag}
                onDragLeave={handleUploadDrag}
                onDragOver={handleUploadDrag}
                onDrop={handleUploadDrop}
              >
                <CloudArrowUpIcon className="w-8 h-8 mb-1" style={{ color: 'var(--Grey-800)' }}/>
                <span className="text-sm text-center" style={{ color: 'var(--Grey-800)' }}>Add images</span>
                <input 
                  id="image-upload" 
                  type="file" 
                  multiple 
                  accept="image/*" 
                  className="hidden" 
                  onChange={handleImageFilesChange} 
                />
              </label>
            </div>
          </div>
        </FormField>
        
        {/* Form Fields - Two column grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <FormField
            label="Ring Name" 
            required={false}
            error={errors.name}
            helperText="What's the name of your ring? Try make it memorable"
          >
            <input
              type="text" 
              value={formData.name || ''}
              onChange={(e) => handleInputChange('name', e.target.value)}
              style={{
                ...styles.input,
                borderColor: errors.name ? 'var(--Stroke-Error)' : 'var(--Input-Border-Default)'
              }}
              placeholder="e.g., Emerald Solitaire Ring"
            />
          </FormField>

          <FormField
            label="'From' Price" 
            required={false}
            error={errors.price}
            helperText="Customers see a budget range based on your “From” price. The upper limit is automatically set at 50% above your entered price"
          >
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    style={{ color: 'var(--Text-Tertiary)' }}>£</span>
              <input
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                step="1"
                min="0"
                value={formData.price || ''}
                onChange={(e) => handlePriceChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === '.' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.currentTarget.blur()}
                style={{
                  ...styles.input,
                  paddingLeft: '2rem',
                  borderColor: errors.price ? 'var(--Stroke-Error)' : 'var(--Input-Border-Default)'
                }}
                placeholder="0.00"
              />
            </div>
          </FormField>

          <FormField
            label="Available to purchase"
            required
            error={errors.availablePurchase}
            helperText="Controls customer guidance for ring enquiries"
          >
            <select
              value={
                formData.availablePurchase === true
                  ? 'true'
                  : formData.availablePurchase === false
                  ? 'false'
                  : ''
              }
              onChange={(e) => {
                const nextValue = e.target.value;
                handleInputChange(
                  'availablePurchase',
                  nextValue === '' ? '' : nextValue === 'true'
                );
              }}
              style={{
                ...styles.input,
                borderColor: errors.availablePurchase
                  ? 'var(--Stroke-Error)'
                  : 'var(--Input-Border-Default)'
              }}
            >
              <option value="" disabled>Select an option</option>
              <option value="true">Yes - made to order</option>
              <option value="false">No - previous commission</option>
            </select>
          </FormField>

          <FormField label="Main Metal Type" helperText="Select the primary metal">
            <select
              value={formData.metalType || ''}
              onChange={(e) => handleInputChange('metalType', e.target.value)}
              style={styles.input}
            >
              <option value="">Select metal type</option>
              {METAL_TYPES.map(metal => <option key={metal} value={metal}>{metal}</option>)}
            </select>
          </FormField>

          <FormField label="Main Gemstone Type" helperText="Select the primary gemstone">
            <select
              value={formData.gemstoneType || ''}
              onChange={(e) => handleInputChange('gemstoneType', e.target.value)}
              style={styles.input}
            >
              <option value="">Select gemstone type</option>
              {GEMSTONE_TYPES.map(gem => <option key={gem} value={gem}>{gem}</option>)}
            </select>
          </FormField>

          <FormField label="Main Gemstone Colour" helperText="Specify the gemstone color">
            <select
              value={formData.gemstoneColor || ''}
              onChange={(e) => handleInputChange('gemstoneColor', e.target.value)}
              style={styles.input}
            >
              <option value="">Select gemstone colour</option>
              {GEMSTONE_COLORS.map(color => <option key={color} value={color}>{color}</option>)}
            </select>
          </FormField>

          <FormField label="Main Setting Type" helperText="How is the gemstone set?">
            <select
              value={formData.settingType || ''}
              onChange={(e) => handleInputChange('settingType', e.target.value)}
              style={styles.input}
            >
              <option value="">Select setting type</option>
              {SETTING_TYPES.map(setting => <option key={setting} value={setting}>{setting}</option>)}
            </select>
          </FormField>
        </div>

        <FormField label="Description" helperText="Describe your product's unique features. Drag bottom right corner to expand!">
          <div style={{ position: 'relative' }}>
            <div style={{ 
              border: '1px solid var(--Input-Border-Default)',
              borderRadius: 'var(--radius-s)',
              background: 'white',
              resize: 'vertical',
              overflow: 'auto',
              minHeight: '220px'  // Changed from 120px to 200px
            }}>
              <ReactQuill 
                theme="snow"
                value={formData.description || ''}
                onChange={(value) => handleInputChange('description', value)}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Tell customers what makes this piece special..."
              />
            </div>
            {/* Simple up/down arrow indicator */}
            <div style={{
              position: 'absolute',
              bottom: '4px',
              right: '4px',
              width: '16px',
              height: '16px',
              pointerEvents: 'none',
              opacity: 0.5
            }}>
              <svg viewBox="0 0 16 16" fill="currentColor" style={{ color: 'var(--Grey-600)' }}>
                {/* Up arrow */}
                <path d="M8 3l3 3H5l3-3z"/>
                {/* Down arrow */}
                <path d="M8 13l3-3H5l3 3z"/>
              </svg>
            </div>
          </div>
        </FormField>

        {/* Actions */}
        <div className="flex justify-between items-center pt-3 mt-4" 
             style={{ borderTop: '1px solid var(--Stroke-Light)' }}>
          <div>
            {!isNew && (
              <button
                type="button"
                onClick={() => onDelete(product.id)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--Red-600)',
                  fontWeight: 'var(--font-weight-medium)',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 'var(--font-size-body3)'
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--Red-700)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--Red-600)'}
              >
                Delete Product
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button 
              type="button"
              onClick={onCancel} 
              style={{
                ...styles.button,
                background: 'var(--Button-Secondary-Enabled-BG)',
                color: 'var(--Button-Secondary-Text)'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--Button-Secondary-Hovered-BG)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--Button-Secondary-Enabled-BG)'}
            >
              Cancel
            </button>
            <button 
              type="button"
              onClick={() => handleSubmit('draft')} 
              style={{
                ...styles.button,
                background: 'var(--Grey-600)',
                color: 'white'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--Grey-700)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--Grey-600)'}
            >
              Save as Draft
            </button>
            <button 
              type="button"
              onClick={() => handleSubmit('live')} 
              disabled={isSubmitting}
              style={{
                ...styles.button,
                background: isSubmitting ? 'var(--Grey-400)' : 'var(--brand-gradient-4)',
                color: 'var(--Grey-900)',
                opacity: isSubmitting ? 0.7 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
              onMouseEnter={e => !isSubmitting && (e.currentTarget.style.background = 'var(--Green-500)')}
              onMouseLeave={e => !isSubmitting && (e.currentTarget.style.background = 'var(--brand-gradient-4)')}
              className="flex items-center justify-center gap-2"
            >
              {isSubmitting && (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              )}
              {isSubmitting ? 'Publishing...' : 'Save & Publish'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductForm;
